import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { PageLoader } from '../components/ui/Spinner';
import { useApp } from '../context/AppContext';
import { fmt, fmtDate, fmtD } from '../utils/format';
import * as api from '../services/api';

// ── Status badge helper ──────────────────────────────────────
function statusBadgeClass(status) {
  if (status === 'paid') return 'badge-success';
  if (status === 'partially_paid') return 'badge-warning';
  return 'badge-outline'; // sent
}

function statusLabel(status) {
  if (status === 'partially_paid') return 'Partial';
  return status;
}

export default function Invoices() {
  const { state, doMarkPaid, doRecordPayment, doDeleteInvoice, cl, toast, fetchInvoices } = useApp();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [selId, setSelId]   = useState(null);
  
  // PDF download loading state
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // Payment form state
  const [payAmount, setPayAmount]   = useState('');
  const [payMethod, setPayMethod]   = useState('');
  const [payNote, setPayNote]       = useState('');
  const [recording, setRecording]   = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);

  // Payment history state
  const [payments, setPayments]         = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [deletingPayId, setDeletingPayId]     = useState(null);

  // Delete invoice state
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const invoiceId = params.get('invoice_id');
    if (invoiceId) {
      setSelId(invoiceId);
      setHasAutoOpened(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (!hasAutoOpened && state.invoices && state.invoices.length > 0) {
      setSelId(state.invoices[0].id);
      setHasAutoOpened(true);
    }
  }, [state.invoices, hasAutoOpened]);

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    const q = search.toLowerCase();
    return state.invoices.filter(inv => {
      const clientName = (cl(inv.client_id)?.name || '').toLowerCase();
      const invNo = (inv.inv_no || '').toLowerCase();
      return clientName.includes(q) || invNo.includes(q);
    });
  }, [state.invoices, search, cl]);

  const selectedInvoice = useMemo(() => {
    return state.invoices.find(inv => inv.id === selId) || null;
  }, [state.invoices, selId]);

  // Fetch payment history when invoice changes
  const loadPayments = useCallback(async (invoiceId) => {
    if (!invoiceId) return;
    setPaymentsLoading(true);
    try {
      const data = await api.getInvoicePayments(invoiceId);
      setPayments(data || []);
    } catch (e) {
      console.error('Failed to load payments:', e);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selId && selectedInvoice) {
      loadPayments(selId);
      // Pre-fill payment amount with balance_due
      setPayAmount(selectedInvoice.balance_due > 0 ? String(selectedInvoice.balance_due) : '');
      setPayMethod('');
      setPayNote('');
      setShowPayForm(false);
    }
  }, [selId, selectedInvoice, loadPayments]);

  // Actions
  async function handleMarkPaid(id) {
    setMarkingPaid(true);
    try {
      // Record a payment entry for the remaining balance so it shows in Payment History
      const inv = state.invoices.find(i => i.id === id);
      const remainingBalance = (inv?.balance_due || inv?.total || 0) - 0;
      if (inv && remainingBalance > 0 && inv.status !== 'paid') {
        await doRecordPayment(id, {
          amount: remainingBalance,
          method: 'Full Settlement',
          note: 'Remaining balance paid in full',
        });
      } else {
        await doMarkPaid(id);
      }
      await loadPayments(id);
    } catch (_) {}
    finally { setMarkingPaid(false); }
  }

  async function handleRecordPayment(id) {
    if (!payAmount || Number(payAmount) <= 0) return;
    setRecording(true);
    try {
      await doRecordPayment(id, {
        amount: Number(payAmount),
        method: payMethod || undefined,
        note: payNote || undefined,
      });
      await loadPayments(id);
      setShowPayForm(false);
      setPayAmount(String(selectedInvoice?.balance_due - Number(payAmount) || 0));
      setPayMethod('');
      setPayNote('');
    } catch (_) {}
    finally { setRecording(false); }
  }

  async function handleDeletePayment(invoiceId, paymentId) {
    setDeletingPayId(paymentId);
    try {
      await api.deleteInvoicePayment(invoiceId, paymentId);
      toast('Payment entry deleted', 'success');
      await Promise.all([loadPayments(invoiceId), fetchInvoices()]);
    } catch (e) {
      toast(`Delete payment failed: ${e.message}`, 'error');
    } finally {
      setDeletingPayId(null);
    }
  }

  async function handleDeleteInvoice(id) {
    if (!window.confirm('Delete this invoice? Work cards will revert to "completed" status.')) return;
    setDeleting(true);
    try {
      await doDeleteInvoice(id);
      setSelId(null);
    } catch (_) {}
    finally { setDeleting(false); }
  }

  async function handleDownloadPdf(id, number) {
    setPdfLoading(true);
    try {
      const data = await api.getInvoicePdfUrl(id);
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (e) {
      console.error('Failed to get PDF URL:', e);
    } finally {
      setPdfLoading(false);
    }
  }

  const isFirstLoad = state.loading.invoices && !state.invoices.length;
  if (isFirstLoad) {
    return <Layout><PageLoader label="Loading invoices directory…" /></Layout>;
  }

  return (
    <Layout>
      <div 
        className={`split-layout invoice-split-layout ${selId ? 'split-open' : ''}`}
        style={{
          gridTemplateColumns: selId ? '1fr 400px' : '1fr'
        }}
      >
        
        {/* ── Left Side: Invoice Directory ── */}
        <div className="pane-left-large glass-card">
          <div className="card-header search-row">
            <h3>Invoices History Directory</h3>
            <input
              type="text"
              placeholder="Search invoices by client or number..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="table-search-input"
            />
          </div>
          <div className="table-wrapper">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Invoice Number</th>
                  <th>Client Name</th>
                  <th>Issue Date</th>
                  <th>Grand Total</th>
                  <th>Paid</th>
                  <th>Status</th>
                  <th className="align-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="placeholder-text">No invoices found.</td>
                  </tr>
                ) : (
                  filteredInvoices.map(inv => {
                    const isSelected = inv.id === selId;
                    const client = cl(inv.client_id);
                    
                    return (
                      <tr
                        key={inv.id}
                        className={isSelected ? 'selected' : ''}
                        onClick={() => setSelId(inv.id)}
                      >
                        <td><strong style={{ fontFamily: 'var(--font-mono)' }}>{inv.inv_no}</strong></td>
                        <td>{client ? client.name : 'Unknown Client'}</td>
                        <td>{fmtDate(inv.inv_date) || inv.inv_date}</td>
                        <td><strong>{fmt(inv.total)}</strong></td>
                        <td>
                          {inv.status === 'paid' ? (
                            <span style={{ color: 'var(--accent-green)', fontSize: 12 }}>{fmt(inv.total)}</span>
                          ) : inv.amount_paid > 0 ? (
                            <span style={{ color: 'var(--accent-orange)', fontSize: 12 }}>{fmt(inv.amount_paid)}</span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${statusBadgeClass(inv.status)}`} style={{ textTransform: 'uppercase' }}>
                            {statusLabel(inv.status)}
                          </span>
                          {inv.status === 'partially_paid' && (
                            <div style={{ fontSize: 10, color: 'var(--accent-orange)', marginTop: 2 }}>
                              {fmt(inv.amount_paid)} of {fmt(inv.total)}
                            </div>
                          )}
                        </td>
                        <td className="align-right">
                          <button className="btn btn-outline" style={{ padding: '6px 12px' }}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Right Side: Selected Invoice Flyout ── */}
        {selId && selectedInvoice && (
          <div className="pane-right-small glass-card" style={{ height: '100%', overflowY: 'auto' }}>
            <div className="invoice-flyout-container">
              <div className="invoice-flyout-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2>{selectedInvoice.inv_no}</h2>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Status: <strong style={{ color: selectedInvoice.status === 'paid' ? 'var(--accent-green)' : selectedInvoice.status === 'partially_paid' ? 'var(--accent-orange)' : 'var(--accent-cyan)' }}>
                      {selectedInvoice.status === 'partially_paid' ? 'PARTIALLY PAID' : selectedInvoice.status.toUpperCase()}
                    </strong>
                  </span>
                </div>
                <button
                  onClick={() => setSelId(null)}
                  className="btn btn-icon-only"
                  title="Close panel"
                  style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px 8px' }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div className="invoice-meta-grid">
                <div>Client Name:<span>{cl(selectedInvoice.client_id)?.name || 'Unknown'}</span></div>
                <div>GSTIN:<span>{cl(selectedInvoice.client_id)?.gstin || 'N/A'}</span></div>
                <div>Issue Date:<span>{fmtDate(selectedInvoice.inv_date) || selectedInvoice.inv_date}</span></div>
                <div>Logged By:<span>{selectedInvoice._raw?.created_by || 'Madhumitha S.'}</span></div>
              </div>

              <div className="invoice-items-breakdown">
                <h4>Billed Job Tickets</h4>
                {(!selectedInvoice.wc_ids || selectedInvoice.wc_ids.length === 0) ? (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No linked work tickets.</p>
                ) : (
                  selectedInvoice.wc_ids.map(cardId => {
                    const wc = state.workcards.find(w => w.id === cardId) || { no: 'Legacy', qty: 0, job: { job_type: 'Printing Job' } };
                    return (
                      <div key={cardId} className="invoice-item-summary-row">
                        <div>
                          <strong>Ticket #{wc.no} — {wc.job?.job_type || 'Printing Job'}</strong>
                          <span>Qty: {(wc.qty || 0).toLocaleString()} sheets | Rate: ₹{(selectedInvoice.rate || 0).toFixed(2)}/sheet</span>
                        </div>
                        <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>
                          ₹{((wc.qty || 0) * (selectedInvoice.rate || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </strong>
                      </div>
                    );
                  })
                )}
              </div>

              {/* ── Amount Breakdown ── */}
              <div className="invoice-amount-audit-card">
                <div><span>Subtotal:</span><strong>{fmt(selectedInvoice.subtotal)}</strong></div>
                <div><span>CGST 9%:</span><strong>{fmt(selectedInvoice.cgst)}</strong></div>
                <div><span>SGST 9%:</span><strong>{fmt(selectedInvoice.sgst)}</strong></div>
                <div className="grand-total">
                  <span>Grand Total:</span>
                  <strong>{fmt(selectedInvoice.total)}</strong>
                </div>
                {selectedInvoice.status !== 'paid' && selectedInvoice.amount_paid > 0 && (
                  <>
                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 8, marginTop: 4 }}>
                      <span>Amount Paid:</span>
                      <strong style={{ color: 'var(--accent-green)' }}>{fmt(selectedInvoice.amount_paid)}</strong>
                    </div>
                    <div style={{ color: 'var(--accent-orange)' }}>
                      <span>Balance Due:</span>
                      <strong>{fmt(selectedInvoice.balance_due)}</strong>
                    </div>
                  </>
                )}
                {selectedInvoice.status === 'sent' && selectedInvoice.amount_paid === 0 && (
                  <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 8, marginTop: 4, color: 'var(--accent-orange)' }}>
                    <span>Balance Due:</span>
                    <strong>{fmt(selectedInvoice.balance_due)}</strong>
                  </div>
                )}
              </div>

              {/* ── Payment Actions ── */}
              <div className="flyout-actions" style={{ flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                  <Button
                    variant="primary"
                    disabled={pdfLoading}
                    onClick={() => handleDownloadPdf(selectedInvoice.id, selectedInvoice.inv_no)}
                    style={{ flex: 1 }}
                  >
                    <i className="fa-solid fa-file-pdf"></i> {pdfLoading ? '…' : 'PDF ↗'}
                  </Button>

                  {selectedInvoice.status === 'paid' ? (
                    <Button variant="outline" disabled style={{ flex: 1, background: 'rgba(0, 230, 118, 0.05)', borderColor: 'rgba(0, 230, 118, 0.2)' }}>
                      <i className="fa-solid fa-circle-check" style={{ color: 'var(--accent-green)' }}></i> Paid In Full
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="success"
                        disabled={markingPaid}
                        onClick={() => handleMarkPaid(selectedInvoice.id)}
                        style={{ flex: 1 }}
                        title="Mark entire balance as paid"
                      >
                        <i className="fa-solid fa-check-double"></i> {markingPaid ? '…' : 'Full Paid'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowPayForm(!showPayForm)}
                        style={{ flex: 1 }}
                      >
                        <i className="fa-solid fa-money-bill-wave"></i> {showPayForm ? 'Cancel' : 'Pay'}
                      </Button>
                    </>
                  )}
                </div>

                {/* ── Partial Payment Form ── */}
                {showPayForm && selectedInvoice.status !== 'paid' && (
                  <div style={{
                    padding: 16, borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-color)', background: 'var(--bg-card)',
                    display: 'flex', flexDirection: 'column', gap: 10, width: '100%'
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                      Record Payment
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Amount (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={payAmount}
                          onChange={e => setPayAmount(e.target.value)}
                          placeholder={`Max: ${selectedInvoice.balance_due}`}
                          style={{
                            width: '100%', padding: '8px 10px', fontSize: 14,
                            background: 'var(--bg-dark)', color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)',
                            fontFamily: 'var(--font-mono)'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Method</label>
                        <select
                          value={payMethod}
                          onChange={e => setPayMethod(e.target.value)}
                          style={{
                            width: '100%', padding: '8px 10px', fontSize: 13,
                            background: 'var(--bg-dark)', color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
                          }}
                        >
                          <option value="">— Select —</option>
                          <option value="cash">Cash</option>
                          <option value="upi">UPI</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cheque">Cheque</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Note (optional)</label>
                      <input
                        type="text"
                        value={payNote}
                        onChange={e => setPayNote(e.target.value)}
                        placeholder="e.g. first installment"
                        style={{
                          width: '100%', padding: '8px 10px', fontSize: 13,
                          background: 'var(--bg-dark)', color: 'var(--text-primary)',
                          border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
                        }}
                      />
                    </div>

                    {Number(payAmount) > selectedInvoice.balance_due && (
                      <div style={{ fontSize: 11, color: 'var(--accent-red)', padding: '4px 0' }}>
                        ⚠️ Amount exceeds balance due ({fmt(selectedInvoice.balance_due)})
                      </div>
                    )}

                    <Button
                      variant="success"
                      disabled={recording || !payAmount || Number(payAmount) <= 0}
                      onClick={() => handleRecordPayment(selectedInvoice.id)}
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      <i className="fa-solid fa-check"></i> {recording ? 'Recording…' : `Record ${fmt(Number(payAmount) || 0)}`}
                    </Button>
                  </div>
                )}

                {/* ── Delete Invoice ── */}
                {selectedInvoice.status !== 'paid' && payments.length === 0 && !paymentsLoading && (
                  <Button
                    variant="outline"
                    onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                    disabled={deleting}
                    style={{ width: '100%', justifyContent: 'center', color: 'var(--accent-red)', borderColor: 'rgba(255, 23, 68, 0.3)' }}
                  >
                    <i className="fa-solid fa-trash"></i> {deleting ? 'Deleting…' : 'Delete Invoice'}
                  </Button>
                )}
              </div>

              {/* ── Payment History ── */}
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="fa-solid fa-clock-rotate-left"></i> Payment History
                </h4>
                {paymentsLoading ? (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</p>
                ) : payments.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No payments recorded yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {payments.map(p => (
                      <div
                        key={p.id}
                        style={{
                          padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.02)',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-green)' }}>
                            {fmt(p.amount)}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {p.method && <span style={{ textTransform: 'capitalize' }}>{p.method.replace('_', ' ')} · </span>}
                            {fmtD(p.paid_at || p.created_at)}
                          </div>
                          {p.note && (
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>
                              "{p.note}"
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeletePayment(selectedInvoice.id, p.id); }}
                          disabled={deletingPayId === p.id}
                          title="Delete this payment"
                          style={{
                            background: 'none', border: 'none', color: 'var(--text-muted)',
                            cursor: 'pointer', fontSize: 13, padding: '4px 6px',
                            opacity: deletingPayId === p.id ? 0.5 : 0.6,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={e => e.target.style.opacity = 1}
                          onMouseLeave={e => e.target.style.opacity = 0.6}
                        >
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}
